import type { Claim, ProblemRecord } from "./types";

/**
 * NHTSA safety / compliance isolation.
 *
 * Deterministic screening of governed Problem Records against the US federal
 * framework:
 *  - 49 CFR 573 — Defect and Noncompliance Responsibility and Reports
 *    (report to NHTSA within 5 working days of a defect/noncompliance decision)
 *  - 49 CFR 579 — Early Warning Reporting (quarterly production/field data)
 *  - 49 CFR 577 — Owner notification
 *  - FMVSS — Federal Motor Vehicle Safety Standards
 *
 * This is an engineering screen that isolates candidates for the safety
 * committee. It is not a legal determination and does not replace the
 * manufacturer's defect decision process.
 */

export type SafetyScope =
  "safety-defect-candidate" | "compliance-candidate" | "monitor" | "out-of-scope";

export interface SafetySystem {
  system: string;
  fmvss: string;
  fmvssTitle: string;
  baseSeverity: number;
}

const SYSTEMS: { match: RegExp; profile: SafetySystem }[] = [
  {
    match: /brake|pedal|abs|stability|trailer brake/i,
    profile: {
      system: "Service brake system",
      fmvss: "FMVSS 135",
      fmvssTitle: "Light vehicle brake systems",
      baseSeverity: 92,
    },
  },
  {
    match: /steer|column|tie rod|rack/i,
    profile: {
      system: "Steering control",
      fmvss: "FMVSS 203/204",
      fmvssTitle: "Steering control system and rearward displacement",
      baseSeverity: 90,
    },
  },
  {
    match: /airbag|restraint|seat belt|belt buckle|pretension/i,
    profile: {
      system: "Occupant crash protection",
      fmvss: "FMVSS 208",
      fmvssTitle: "Occupant crash protection",
      baseSeverity: 95,
    },
  },
  {
    match: /fuel|tank|injector|fuel pump/i,
    profile: {
      system: "Fuel system integrity",
      fmvss: "FMVSS 301",
      fmvssTitle: "Fuel system integrity",
      baseSeverity: 88,
    },
  },
  {
    match: /latch|door|tailgate|liftgate|hinge/i,
    profile: {
      system: "Door latches and retention",
      fmvss: "FMVSS 206",
      fmvssTitle: "Door locks and door retention components",
      baseSeverity: 80,
    },
  },
  {
    match: /hv battery|high voltage|charger|coolant|thermal|inverter|contactor/i,
    profile: {
      system: "Electric powertrain / thermal propagation",
      fmvss: "FMVSS 305",
      fmvssTitle: "Electric-powered vehicles: electrolyte spillage and electrical shock protection",
      baseSeverity: 90,
    },
  },
  {
    match: /suspension|strut|spring|control arm|knuckle|mount/i,
    profile: {
      system: "Suspension and vehicle control",
      fmvss: "FMVSS 126",
      fmvssTitle: "Electronic stability control systems (vehicle control)",
      baseSeverity: 78,
    },
  },
  {
    match: /lamp|light|headlamp|wiper|defrost|mirror|camera|rear ?view/i,
    profile: {
      system: "Visibility and signalling",
      fmvss: "FMVSS 108/111",
      fmvssTitle: "Lamps, reflective devices and rear visibility",
      baseSeverity: 76,
    },
  },
  {
    match: /seat|heater mat|bolster|track/i,
    profile: {
      system: "Seating systems",
      fmvss: "FMVSS 202a/207",
      fmvssTitle: "Head restraints and seating systems",
      baseSeverity: 70,
    },
  },
  {
    match: /display|head unit|cluster|telematics|infotainment|software|firmware/i,
    profile: {
      system: "Driver information / telltales",
      fmvss: "FMVSS 101",
      fmvssTitle: "Controls and displays",
      baseSeverity: 62,
    },
  },
];

const HAZARD_WORDS =
  /fire|smoke|thermal runaway|loss of|stall|separat|fracture|crack|unintended|open in motion|leak|short circuit|overheat|melt|no braking|reduced braking|inoperative/i;

const DEFAULT_SYSTEM: SafetySystem = {
  system: "Non-safety functional system",
  fmvss: "—",
  fmvssTitle: "No directly applicable FMVSS identified",
  baseSeverity: 40,
};

export const classifySystem = (r: ProblemRecord): SafetySystem => {
  const text = `${r.component} ${r.title} ${r.failureCause}`;
  return SYSTEMS.find((s) => s.match.test(text))?.profile ?? DEFAULT_SYSTEM;
};

/** Add N US working days (Mon–Fri) to a date. */
export function addWorkingDays(from: Date, days: number) {
  const d = new Date(from);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return d;
}

export const ewrQuarter = (iso: string) => {
  const d = new Date(iso);
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
};

export interface SafetyAssessment {
  record: ProblemRecord;
  system: SafetySystem;
  scope: SafetyScope;
  safetyScore: number;
  hazardCue: boolean;
  claimCount: number;
  hazardClaims: number;
  firstKnowledge: string;
  part573Due: string;
  daysToDue: number;
  clockStatus: "not-triggered" | "open" | "due-soon" | "breached" | "filed";
  ewrQuarter: string;
  containmentVerified: boolean;
  ownerNotificationNeeded: boolean;
  rationale: string[];
  obligations: {
    ref: string;
    title: string;
    requirement: string;
    state: "met" | "open" | "at-risk" | "n/a";
  }[];
}

export function assessSafety(
  r: ProblemRecord,
  claims: Claim[],
  today = new Date(),
): SafetyAssessment {
  const system = classifySystem(r);
  const mine = claims.filter((c) => c.recordId === r.id);
  const hazardClaims = mine.filter((c) => HAZARD_WORDS.test(c.description)).length;
  const hazardCue =
    HAZARD_WORDS.test(`${r.title} ${r.failureCause} ${r.description}`) || hazardClaims > 0;

  // Severity of the affected system is only half the picture — field evidence and
  // the governed risk rating decide whether a record leaves the monitor band.
  let score = system.baseSeverity;
  if (hazardCue) score += 6;
  if (r.risk === "critical") score += 8;
  else if (r.risk === "high") score += 2;
  else if (r.risk === "medium") score -= 12;
  else score -= 24;
  if (r.claims >= 400) score += 4;
  else if (r.claims < 100) score -= 8;
  if (r.status === "closed") score -= 25;
  score = Math.max(0, Math.min(100, Math.round(score)));

  const hasStandard = system.fmvss !== "—";
  const liveRisk = r.status !== "closed" && (r.risk === "critical" || r.risk === "high");
  const scope: SafetyScope = !hasStandard
    ? score >= 60
      ? "monitor"
      : "out-of-scope"
    : score >= 88 && liveRisk && hazardCue
      ? "safety-defect-candidate"
      : score >= 78 && liveRisk
        ? "compliance-candidate"
        : "monitor";

  const reported = mine
    .map((c) => c.reportedDate)
    .sort()
    .at(0);
  const firstKnowledge = reported && reported < r.openedAt ? reported : r.openedAt;
  const dueDate = addWorkingDays(new Date(firstKnowledge), 5);
  const part573Due = dueDate.toISOString().slice(0, 10);
  const daysToDue = Math.ceil((dueDate.getTime() - today.getTime()) / 86_400_000);

  const triggered = scope === "safety-defect-candidate" || scope === "compliance-candidate";
  // Records that have already moved past containment are treated as reported —
  // the clock only runs while the record is still in signal or containment.
  const clockRunning = r.status === "signal" || r.status === "containment";
  const clockStatus: SafetyAssessment["clockStatus"] = !triggered
    ? "not-triggered"
    : !clockRunning
      ? "filed"
      : daysToDue < 0
        ? "breached"
        : daysToDue <= 2
          ? "due-soon"
          : "open";

  const containmentVerified =
    r.containment.length > 0 && r.containment.every((c) => c.status === "verified");

  const rationale: string[] = [
    `${system.system} — screened against ${system.fmvss} (${system.fmvssTitle}).`,
    `${r.claims.toLocaleString()} field claims across ${r.unitsInService.toLocaleString()} units in service.`,
  ];
  if (hazardCue)
    rationale.push(
      `Hazard language present in the failure description${hazardClaims ? ` and in ${hazardClaims} claim narratives` : ""}.`,
    );
  if (r.risk === "critical" || r.risk === "high")
    rationale.push(`Governed risk rating is ${r.risk} (score ${r.riskScore}).`);
  if (!containmentVerified)
    rationale.push("Customer-protection containment is not fully verified across all scopes.");

  const obligations: SafetyAssessment["obligations"] = [
    {
      ref: "49 CFR 573.6",
      title: "Defect / noncompliance information report",
      requirement:
        clockStatus === "filed"
          ? `Filed — 5 working days from first knowledge ran to ${part573Due}`
          : `File within 5 working days of a decision — due ${part573Due}`,
      state: !triggered
        ? "n/a"
        : clockStatus === "filed"
          ? "met"
          : clockStatus === "breached"
            ? "at-risk"
            : "open",
    },
    {
      ref: "49 CFR 573.13",
      title: "Quarterly recall status report",
      requirement: "Six quarterly reports once a recall is filed",
      state: triggered ? "open" : "n/a",
    },
    {
      ref: "49 CFR 577",
      title: "Owner notification",
      requirement: "Notify owners within 60 days of the 573 report",
      state: scope === "safety-defect-candidate" ? "open" : "n/a",
    },
    {
      ref: "49 CFR 579.21",
      title: "Early Warning Reporting",
      requirement: `Include claim counts in the ${ewrQuarter(firstKnowledge)} EWR submission`,
      state: "open",
    },
    {
      ref: "49 CFR 573.6(c)(8)",
      title: "Interim customer protection",
      requirement: "Containment verified at plant, transit, dealer stock and supplier",
      state: containmentVerified ? "met" : "at-risk",
    },
  ];

  return {
    record: r,
    system,
    scope,
    safetyScore: score,
    hazardCue,
    claimCount: mine.length,
    hazardClaims,
    firstKnowledge,
    part573Due,
    daysToDue,
    clockStatus,
    ewrQuarter: ewrQuarter(firstKnowledge),
    containmentVerified,
    ownerNotificationNeeded: scope === "safety-defect-candidate" && r.status !== "closed",
    rationale,
    obligations,
  };
}

export const assessAll = (records: ProblemRecord[], claims: Claim[], today = new Date()) =>
  records.map((r) => assessSafety(r, claims, today)).sort((a, b) => b.safetyScore - a.safetyScore);

export const SCOPE_LABEL: Record<SafetyScope, string> = {
  "safety-defect-candidate": "Safety defect candidate",
  "compliance-candidate": "Compliance candidate",
  monitor: "Monitor",
  "out-of-scope": "Out of scope",
};
